import React, { useState } from 'react';
import { Box } from 'rebass';
import { useFormikContext } from 'formik';
import { Intensity, Setting } from '@cssat/acorn-service-delivery';
import { FormFields } from './validation';
import { Button } from '../../../components/styled/button';
import {
  Checkbox,
  DateField,
  Radio,
  RadioGroupField,
  TextareaField,
  ErrorMessage,
} from '../../../components/styled/forms';
import { ReactComponent as AddIcon } from '../../../icons/add.svg';
import { LegendaryLabel } from '../../../components/styled/forms';
import { colors, space } from '../../../theme';
import { SecondaryBox } from '../../../components/styled/cards';
import { ServicePlan } from '../../../types';
import {
  presentIntensity,
  presentSetting,
} from '../../../enumPresentation/services';

const durationIntervalOptions = [15, 30, 45, 60, 75, 90, 105, 120] as const;

const intensityOptions = [Intensity.Individual, Intensity.Group] as const;

const settingOptions = [
  Setting.Telepractice,
  Setting.AgencySponsoredGroup,
  Setting.ChildCareCenter,
  Setting.CommunityGroup,
  Setting.HomeVisit,
  Setting.ClinicVisit,
  Setting.Correspondence,
  Setting.OfficeVisit,
  Setting.PhoneCall,
] as const;

export const SectionOption03WithChanges: React.FC<{
  useServicePlan: [
    ServicePlan | undefined,
    React.Dispatch<React.SetStateAction<ServicePlan | undefined>>
  ];
}> = ({ useServicePlan }) => {
  const [service] = useServicePlan;

  const [noteAdded, setNoteAdded] = useState<boolean>(false);

  const { values, touched, errors } = useFormikContext<FormFields>();

  return (
    <>
      <DateField name="dateOfService" label="Date delivered" />
      {noteAdded ? (
        <Box mt={space.two}>
          <TextareaField
            autoFocus={true}
            name="note"
            label="Note"
            data-testid="track-session-notes"
            onBlur={e => {
              if (e.target.value.trim() === '') {
                setNoteAdded(false);
              }
            }}
          />
        </Box>
      ) : (
        <Button
          mt={space.one}
          variant="text"
          icon={AddIcon}
          onClick={() => setNoteAdded(true)}
          minWidth={['auto', 'auto', 'auto']}
        >
          Add note (optional)
        </Button>
      )}
      <LegendaryLabel variant="bigLabel" mb={space.one} mt={space.one}>
        What changed from the plan?
      </LegendaryLabel>
      {touched['whatChanged'] && errors['whatChanged'] && (
        <ErrorMessage
          display="block"
          mb={space.one}
          errors={errors}
          name={'whatChanged'}
          errorName="whatChangedError"
        />
      )}
      <Checkbox
        label="Session length"
        name="whatChanged"
        value="duration"
        mb={space.one}
      />
      {values['whatChanged'] && values['whatChanged'].includes('duration') && (
        <SecondaryBox mb={space.one}>
          <RadioGroupField name="duration" variant="radioPillGroup">
            <LegendaryLabel
              css={{ flexBasis: '100%' }}
              mb={space.one}
              color={colors.deepTeal}
            >
              How long was the session?
            </LegendaryLabel>
            <>
              {durationIntervalOptions
                .filter(duration => service && service.duration !== duration)
                .map(duration => (
                  <Radio
                    key={`${duration}min`}
                    label={`${duration} min`}
                    value={duration}
                    variant="radioPill"
                  />
                ))}
            </>
          </RadioGroupField>
        </SecondaryBox>
      )}
      <Checkbox
        label="Type"
        name="whatChanged"
        value="intensity"
        mb={space.one}
      />
      {values['whatChanged'] && values['whatChanged'].includes('intensity') && (
        <SecondaryBox mb={space.one}>
          <RadioGroupField name="intensity" variant="radioPillGroup">
            <LegendaryLabel
              css={{ flexBasis: '100%' }}
              mb={space.one}
              color={colors.deepTeal}
            >
              What was the service type?
            </LegendaryLabel>
            <>
              {intensityOptions
                .filter(intensity => service && service.intensity !== intensity)
                .map(intensity => (
                  <Radio
                    key={intensity}
                    label={presentIntensity(intensity)}
                    value={intensity}
                    variant="radioPill"
                  />
                ))}
            </>
          </RadioGroupField>
        </SecondaryBox>
      )}
      <Checkbox
        label="Setting"
        name="whatChanged"
        value="setting"
        mb={space.one}
      />
      {values['whatChanged'] && values['whatChanged'].includes('setting') && (
        <SecondaryBox>
          <RadioGroupField name="setting">
            <LegendaryLabel
              css={{ flexBasis: '100%' }}
              mb={space.one}
              color={colors.deepTeal}
            >
              What was the setting?
            </LegendaryLabel>
            <>
              {settingOptions
                .filter(setting => service && service.setting !== setting)
                .map(setting => (
                  <Radio
                    key={`setting-${setting}`}
                    label={presentSetting(setting)}
                    value={setting}
                  />
                ))}
            </>
          </RadioGroupField>
        </SecondaryBox>
      )}
    </>
  );
};
